import { Box, Button, DialogContent, FormControl, Grid, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import { useId } from "react";
import useCreateUserHook from "../../hooks/useCreateUserHook";
import useBreakpoint from "../../utils/useBreakpoint";
import InputFormik from "../inputs/InputFormik";
import SelectCountryCodes from "../inputs/SelectCountryCodes";
import UserAutocomplete from "../inputs/UserAutocomplete";
import UserRole from "../../interfaces/UserRole";
import type { Referente } from "../../firebase/firestore/usuarios";

export default function FormCreateUser({
    onClose,
}: {
    onClose: () => void;
}) {
    const bp = useBreakpoint();
    const roleId = useId();
    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        handleSubmit,
        loading,
    } = useCreateUserHook(onClose);

    const handleSubmitAndCloseModal = async () => {
        await handleSubmit();
    }

    return (
        <DialogContent sx={{ maxWidth: "100%" }}>
            <Grid container spacing={4} sx={{ paddingX: 4, paddingTop: 2 }}>
                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Nombre"
                        name="name"
                        value={values.name}
                        error={!!touched.name && !!errors.name}
                        errorText={errors.name ?? ""}
                        handleChange={handleChange("name")}
                        handleBlur={handleBlur("name")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Apellido"
                        name="lastName"
                        value={values.lastName}
                        error={!!touched.lastName && !!errors.lastName}
                        errorText={errors.lastName ?? ""}
                        handleChange={handleChange("lastName")}
                        handleBlur={handleBlur("lastName")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Correo"
                        name="email"
                        value={values.email}
                        error={!!touched.email && !!errors.email}
                        errorText={errors.email ?? ""}
                        handleChange={handleChange("email")}
                        handleBlur={handleBlur("email")}
                        type="email"
                        autoCapitalize="none"
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <SelectCountryCodes
                            label={"Cód."}
                            name={"countryCode"}
                            value={values.countryCode}
                            error={!!touched.countryCode && !!errors.countryCode}
                            errorText={errors.countryCode ?? ""}
                            setFieldValue={setFieldValue}
                            handleBlur={handleBlur("countryCode")}
                        />

                        <InputFormik
                            label="Teléfono"
                            name="phone"
                            value={values.phone}
                            error={!!touched.phone && !!errors.phone}
                            errorText={errors.phone ?? ""}
                            handleChange={handleChange("phone")}
                            handleBlur={handleBlur("phone")}
                            style={{ width: bp === "xs" || bp === "sm" ? "60%" : "70%" }}
                        />
                    </Stack>
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <FormControl fullWidth error={!!touched.role && !!errors.role}>
                        <InputLabel id={roleId}>Rol</InputLabel>
                        <Select
                            labelId={roleId}
                            id={roleId}
                            value={values.role}
                            label="Rol"
                            onChange={(e) => setFieldValue("role", e.target.value as UserRole)}
                            onBlur={handleBlur("role")}
                            size="small"
                        >
                            <MenuItem value={UserRole.Admin}>Admin</MenuItem>
                            <MenuItem value={UserRole.Digitador}>Digitador</MenuItem>
                            <MenuItem value={UserRole.Cliente}>Cliente</MenuItem>
                        </Select>
                        {touched.role && errors.role && (
                            <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5, ml: 1.75 }}>
                                {errors.role}
                            </Box>
                        )}
                    </FormControl>
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Contraseña"
                        name="password"
                        value={values.password}
                        error={!!touched.password && !!errors.password}
                        errorText={errors.password ?? ""}
                        handleChange={handleChange("password")}
                        handleBlur={handleBlur("password")}
                        type="password"
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <UserAutocomplete
                        label="Vendedor"
                        value={values.vendedor as Referente | undefined}
                        onChange={(ref) => setFieldValue("vendedor", ref)}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <UserAutocomplete
                        label="Referido por"
                        value={values.referidoPor as Referente | undefined}
                        onChange={(ref) => setFieldValue("referidoPor", ref)}
                    />
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Button onClick={onClose} sx={{ mr: 1 }} disabled={loading}>
                        Cancelar
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button onClick={handleSubmitAndCloseModal} disabled={loading}>
                        {loading ? "Creando..." : "Crear"}
                    </Button>
                </Grid>
            </Grid>
        </DialogContent>
    )
}
